'use client';

// ContentLimitWarning - Warning component when content exceeds recommended limits

import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslations } from 'next-intl';
import type { ContentSource } from '../../types/content-source';
import { formatNumber } from '../../lib/format';

export interface ContentLimitWarningProps {
  /** Total character count */
  totalCharacters: number;
  /** Content sources (for showing largest sources) */
  sources: ContentSource[];
  /** Character limit threshold (default: 150,000) */
  limit?: number;
  /** Maximum number of largest sources to show */
  maxLargestSources?: number;
  /** Callback to delete a content source */
  onDelete?: (contentId: string) => void;
}

export function ContentLimitWarning({
  totalCharacters,
  sources,
  limit = 150_000,
  maxLargestSources = 3,
  onDelete,
}: ContentLimitWarningProps) {
  const t = useTranslations('creator.contentSources.warning');
  const [expanded, setExpanded] = useState(false);

  // Don't show warning if under limit
  if (totalCharacters <= limit) {
    return null;
  }

  // Get largest sources sorted by character count
  const largestSources = [...sources]
    .sort((a, b) => b.characterCount - a.characterCount)
    .slice(0, maxLargestSources);

  return (
    <Alert
      severity="warning"
      sx={{ mb: 2 }}
    >
      <AlertTitle>{t('contentLimit')}</AlertTitle>
      <Typography variant="body2">
        {t('contentLimitMessage')}
      </Typography>

      {/* Collapsible largest sources section */}
      {largestSources.length > 0 && (
        <>
          <Box
            onClick={() => setExpanded(!expanded)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              mt: 1,
              gap: 0.5,
            }}
          >
            <Typography variant="caption" fontWeight="medium">
              {t('largestSources')}
            </Typography>
            <IconButton size="small" sx={{ p: 0 }}>
              {expanded ? (
                <ExpandLessIcon sx={{ fontSize: 16 }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Box>
          <Collapse in={expanded}>
            <List dense disablePadding sx={{ mt: 0.5 }}>
              {largestSources.map((source) => (
                <ListItem key={source.id} sx={{ py: 0.25, px: 0 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">
                          {'\u2022'} {source.title} ({formatNumber(source.characterCount)} chars)
                        </Typography>
                        {onDelete && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="error"
                            sx={{
                              cursor: 'pointer',
                              ml: 1,
                              textDecoration: 'underline',
                              '&:hover': { color: 'error.dark' },
                              flexShrink: 0,
                            }}
                            onClick={() => onDelete(source.id)}
                          >
                            {t('delete')}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </>
      )}
    </Alert>
  );
}

export default ContentLimitWarning;
